import React, { useContext, useEffect, useState } from "react";

import { useTable, useSortBy, useRowSelect } from "react-table";
import { useExportData } from "react-table-plugins";

import { FaFilePdf, FaFileExcel, FaFileCsv } from "react-icons/fa";

import { Grid, Button, Fade, Alert, Tooltip, Checkbox } from "@mui/material";
import { Popover, Typography } from "@mui/material";

import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";

import { CustomButtonPlusIcon } from "../Design/CustomButtonPlusIcon";
import { PermissionContext } from "../../Context/Context";

import { Add, DeleteForever, InfoOutlined, Warning } from "@mui/icons-material";

import ReactPaginate from "react-paginate";

import {
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  LightbulbOff,
  ZoomIn,
  ZoomOut,
  ChevronUp,
  ChevronDown,
  Delete,
} from "lucide-react";
import { Box, minWidth } from "@mui/system";
import LoadingPage from "../Loading/LoadingPage";

const Table = ({
  columns,
  data,
  view,
  viewTitle,
  tableWidth,
  minTableWidth,

  sortColumn = null,
  sortDirection = null,

  onSort = () => {},

  deleteSelected,
  selectedRows,
  setSelectedRows = () => {},
  setChangeDone = () => {},
  changeDone,
  flagToShow,
  icon: Icon,
  viewPermission = "",
  selectionActions,
  apiRoute = "",
  checkBoxFlag = false,
  sxPropContainer = {},
  sxDarkColor = {},
  mainView = "",

  pageCount = 0,
  forcePage = 1,
  onPageChange = () => {},
  totalPagesNumber = 0,
  objPerPage = 10,
  isLoading = false,

  // SubComponentContactsReport,
  totalsReport = [],
  tableReports = false,

  // FLAGS

  flagNoCreate = false,
}) => {
  /* ================= COLORS ================= */
  const [isDark, setIsDark] = useState(mainView === "informes");
  /* ================= SORT STATE ================= */
  const [blurSensitive, setBlurSensitive] = useState(false);
  const STORAGE_KEY = `table_state_${viewTitle}`;

  const [tableState, setTableState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);

    return saved
      ? JSON.parse(saved)
      : {
          sortColumn: null,
          sortDirection: null,
        };
  });

  const pastelColors = {
    contactos: {
      header: "#0d7bc8ff",
      headerHeavy:
        "linear-gradient(to right, rgb(10, 95, 160) 0%, rgba(10, 95, 160, 0.40) 30%, #ffffff00 100%)",
      rowHover: "#e6f2fb",
    },

    expedientes: {
      header: "#2e7d32",
      headerHeavy:
        "linear-gradient(to right, rgb(46, 125, 50) 0%, rgb(46, 125, 50, 0.40) 30%, #ffffff00 100%)",
      rowHover: "#e8f5e9",
    },

    clientes: {
      header: "rgb(167 103 0)",
      headerHeavy:
        "linear-gradient(to right, rgb(122, 77, 0) 0%, rgb(122, 77, 0, 0.40) 30%, #ffffff00 100%)",
      rowHover: "rgb(255 157 0 / 20%)",
    },

    default: {
      header: "rgb(118 118 118)",
      headerHeavy:
        "linear-gradient(to right, rgba(75,85,99) 0%, rgba(75,85,99, 0.40) 30%, #ffffff00 100%)",
      rowHover: "#f5f5f5",
    },
  };

  const { header, headerHeavy, rowHover } =
    pastelColors[viewTitle] || pastelColors.default;

  const lightTheme = {
    headerBg: "#fff",
    headerBgHeavy: headerHeavy,
    headerText: header,
    rowEven: "#ffffff",
    rowOdd: "#ffffff",
    rowHover: rowHover,
    text: "#1f2937",
    border: "#e5e7eb",
  };
  const darkTheme = {
    headerBgHeavy:
      "linear-gradient(to right, #00000034 0%, rgba(75,85,99, 0.40) 30%, #00000034 100%)",
    headerBg: "#1f2937",
    headerText: "#e5e7eb",
    rowEven: "#111827",
    rowOdd: "#1f2937",
    rowHover: "#060a12",
    text: "#e5e7eb",
    border: "#374151",
  };

  const theme = isDark ? darkTheme : lightTheme;

  const { can } = useContext(PermissionContext);

  const [fontSize, setFontSize] = useState(14);

  const [infoAnchor, setInfoAnchor] = useState(null);

  const handleOpenInfo = (event) => {
    setInfoAnchor(event.currentTarget);
  };

  const handleCloseInfo = () => {
    setInfoAnchor(null);
  };

  const openInfo = Boolean(infoAnchor);

  const formatExportDateTime = (value) => {
    if (!value) return "";

    const parsed = dayjs(value);
    if (!parsed.isValid()) return "";

    if (parsed.hour() === 0 && parsed.minute() === 0 && parsed.second() === 0) {
      return parsed.format("DD/MM/YYYY");
    }

    return parsed.format("DD/MM/YYYY HH:mm");
  };

  const normalizeExpedientesExportValue = (column, value) => {
    if (viewTitle !== "expedientes") return value;

    const field = column?.id;

    if (field === "apud_acta" || field === "peritaje") {
      if (value === 1 || value === "1" || value === true) return "SI";
      if (value === 0 || value === "0" || value === false) return "NO";
      return "Sin asignar";
    }

    if (field === "trial_date_time") {
      if (value && typeof value === "object") {
        return formatExportDateTime(value?.date_end);
      }

      return formatExportDateTime(value);
    }

    return value;
  };

  const getNormalizedExportRows = (columns, rowsData) => {
    return (rowsData || []).map((row) => {
      return (row || []).map((value, index) => {
        const column = columns?.[index];
        return normalizeExpedientesExportValue(column, value);
      });
    });
  };

  /* ================= EXPORT ================= */

  const getExportFileBlob = ({ columns, data, fileType, fileName }) => {
    const normalizedRows = getNormalizedExportRows(columns, data);

    if (fileType === "csv") {
      const headerNames = columns.map((col) => col.exportValue);
      const csvString = Papa.unparse({
        fields: headerNames,
        data: normalizedRows,
      });
      return new Blob([csvString], { type: "text/csv" });
    }

    if (fileType === "xlsx") {
      const header = columns.map((c) => c.exportValue);

      const compatibleData = normalizedRows.map((row) => {
        const obj = {};
        header.forEach((col, index) => {
          obj[col] = row[index];
        });
        return obj;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(compatibleData, { header });

      XLSX.utils.book_append_sheet(wb, ws, "React Table Data");

      XLSX.writeFile(wb, `${fileName}.xlsx`);

      return false;
    }

    if (fileType === "pdf") {
      const headerNames = columns.map((column) => column.exportValue);

      const doc = new JsPDF();

      doc.autoTable({
        head: [headerNames],
        body: normalizedRows,
        styles: { fontSize: 6 },
        theme: "grid",
      });

      doc.save(`${fileName}.pdf`);

      return false;
    }

    return false;
  };

  /* ================= TABLE ================= */

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    exportData,
    visibleColumns,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data,
      getExportFileBlob,
      manualSortBy: true,
      disableSortRemove: false,
      autoResetSortBy: false,
    },
    useSortBy,
    useRowSelect,
    useExportData,
    (hooks) => {
      hooks.visibleColumns.push((cols) => {
        if (!checkBoxFlag) return cols;

        return [
          {
            id: "selection",
            disableSortBy: true,
            Header: ({ getToggleAllRowsSelectedProps }) => {
              const { indeterminate, ...rest } =
                getToggleAllRowsSelectedProps();

              return (
                <Checkbox
                  {...rest}
                  indeterminate={!!indeterminate}
                  sx={{
                    p: 0.5,
                    color: "#0d7bc8",
                    "&.Mui-checked": {
                      color: "#0d7bc8",
                    },
                    "&.MuiCheckbox-indeterminate": {
                      color: "#0d7bc8",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 18,
                    },
                  }}
                />
              );
            },
            Cell: ({ row }) => {
              const { indeterminate, ...rest } =
                row.getToggleRowSelectedProps();

              return (
                <Checkbox
                  {...rest}
                  indeterminate={!!indeterminate}
                  sx={{
                    p: 0.5,
                    color: "#0d7bc8",
                    "&.Mui-checked": {
                      color: "#0d7bc8",
                    },
                    "&.MuiCheckbox-indeterminate": {
                      color: "#0d7bc8",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 18,
                    },
                  }}
                />
              );
            },
          },
          ...cols,
        ];
      });
    },
  );

  /* ================= SORT ================= */
  const handleSort = (column) => {
    const field = column.sortField || column.id;
    if (!field) return;

    // console.log("COLUMN CLICKED:", field);
    // console.log("STATE BEFORE:", tableState);

    let direction = "DESC";

    if (tableState.sortColumn === field) {
      if (tableState.sortDirection === "DESC") direction = "ASC";
      else if (tableState.sortDirection === "ASC") direction = null;
    }

    // console.log("NEW DIRECTION:", direction);

    const newState = {
      sortColumn: direction ? field : null,
      sortDirection: direction,
    };

    // console.log("STATE AFTER:", newState);

    setTableState(newState);

    onSort?.(newState.sortColumn, newState.sortDirection);
  };

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tableState));
  }, [tableState]);

  useEffect(() => {
    if (sortColumn !== undefined && sortDirection !== undefined) {
      setTableState({
        sortColumn,
        sortDirection,
      });
    }
  }, [sortColumn, sortDirection]);

  useEffect(() => {
    if (setSelectedRows) {
      // Aquí pasamos los datos seleccionados al estado externo
      setSelectedRows(selectedFlatRows.map((row) => row.original));
    }
  }, [selectedFlatRows, setSelectedRows]);

  /* ================= RENDER ================= */

  const glassButtonBase = {
    width: "26px",
    height: "26px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backdropFilter: "blur(6px)",
    transition: "all 0.2s ease",
  };

  const getGlassButtonTheme = (isDark) => ({
    background: isDark ? "rgba(255, 255, 255, 0.23)" : "rgba(0,0,0,0.05)",

    "&:hover": {
      background: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
      transform: "scale(1.05)",
    },
  });

  const popoverText = (isDark) => ({
    fontSize: "13px",
    lineHeight: 1.5,
    color: isDark ? "#e5e7eb" : "#1f2937",
  });

  const popoverTitle = (isDark) => ({
    fontWeight: 600,
    marginBottom: "6px",
    color: isDark ? "#e5e7eb" : "#1f2937",
  });

  return (
    <Fade in timeout={800}>
      <div>
        {/* ACTION BAR */}

        <Box
          sx={{
            m: "10px",
            borderRadius: "10px",
          }}
        >
          <Grid
            container
            spacing={1}
            sx={{ flexWrap: "wrap", borderRadius: "10px" }}
          >
            {/* <Grid
              item
              xs={12}
              md={mainView == "informes" ? 4 : 5}
              sx={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
              }}>
              {selectedFlatRows.length > 0 && (
                <>
                  <Tooltip
                    title="Eliminar los registros seleccionados"
                    arrow
                    placement="top">
                    <Button
                      startIcon={<DeleteForever />}
                      color="error"
                      size="small"
                      variant="contained"
                      sx={{
                        boxShadow: "none",
                        height: "33px",
                      }}
                      onClick={() => deleteSelected(selectedFlatRows)}>
                      <span style={{ color: "white", paddingTop: "4px" }}>
                        ELIMINAR SELECCIONADOS
                      </span>
                    </Button>
                  </Tooltip>

                  {selectionActions}
                </>
              )}
            </Grid> */}

            <Grid
              item
              xs={12}
              // md={mainView == "informes" ? 12 : 7}
              sx={{
                display: "flex",
                justifyContent: "end",
                gap: "8px",
                flexWrap: "wrap",
                mt: "10px",
              }}
            >
              {/* MODO CLARO / OSCURO */}
              <Tooltip title="Modo oscuro" arrow placement="top">
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setIsDark((prev) => !prev)}
                  sx={{
                    minWidth: "40px",
                    backgroundColor: isDark ? "#ffffff" : "#000000",
                    color: isDark ? "#1f2937" : "#ffffff",
                    border: isDark ? "1px solid #d1d5db" : "none",
                    boxShadow: "none",
                    height: "33px",

                    "&:hover": {
                      backgroundColor: isDark ? "#f3f4f6" : "#000000",
                      boxShadow: "none",
                    },
                  }}
                >
                  {isDark ? <Lightbulb /> : <LightbulbOff />}
                </Button>
              </Tooltip>

              {/* REDUCIR TEXTO */}
              <Tooltip
                title="Reducir tamaño del texto de la tabla"
                arrow
                placement="top"
              >
                <Button
                  onClick={() => setFontSize((p) => Math.max(p - 1, 10))}
                  color="secondary"
                  sx={{
                    height: "33px",
                  }}
                  variant={isDark ? "contained" : "outlined"}
                >
                  <ZoomOut />
                </Button>
              </Tooltip>

              {/* AUMENTAR TEXTO */}
              <Tooltip
                title="Aumentar tamaño del texto de la tabla"
                arrow
                placement="top"
              >
                <Button
                  onClick={() => setFontSize((p) => Math.min(p + 1, 24))}
                  color="secondary"
                  sx={{
                    height: "33px",
                  }}
                  variant={isDark ? "contained" : "outlined"}
                >
                  <ZoomIn />
                </Button>
              </Tooltip>

              {/* EXPORT CSV */}
              <Tooltip
                title="Exportar datos de la tabla a CSV"
                arrow
                placement="top"
              >
                <div>
                  <CustomButtonPlusIcon
                    icon={FaFileExcel}
                    text="CSV"
                    color={"warning"}
                    variant={"contained"}
                    handleFunction={() => exportData("csv", false)}
                    position={"start"}
                    sxProps={{ height: "33px" }}
                  />
                </div>
              </Tooltip>

              {/* EXPORT XLSX */}
              <Tooltip
                title="Exportar datos de la tabla a Excel (XLSX)"
                arrow
                placement="top"
              >
                <div>
                  <CustomButtonPlusIcon
                    icon={FaFileCsv}
                    color="success"
                    variant={isDark ? "contained" : ""}
                    text="XLSX"
                    handleFunction={() => exportData("xlsx", false)}
                    position={"start"}
                    sxProps={{ height: "33px" }}
                  />
                </div>
              </Tooltip>

              {/* EXPORT PDF */}
              <Tooltip
                title="Exportar datos de la tabla a PDF"
                arrow
                placement="top"
              >
                <div>
                  <CustomButtonPlusIcon
                    color="error"
                    icon={FaFilePdf}
                    variant={"contained"}
                    text="PDF"
                    handleFunction={() => exportData("pdf", false)}
                    position={"start"}
                    sxProps={{ height: "33px" }}
                  />
                </div>
              </Tooltip>

              {/* CREAR */}
              {Icon && can(viewPermission, "add") && !flagNoCreate && (
                <Tooltip title={`Crear nuevo ${view}`} arrow placement="top">
                  <div>
                    <CustomButtonPlusIcon
                      icon={Icon || <Add />}
                      to={`/${viewTitle}/crear`}
                      text={`Crear ${view}`}
                      position={"start"}
                      variant={"contained"}
                      sxProps={{ height: "33px" }}
                    />
                  </div>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* CONTADOR FIJO */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            // margin: "20px 10px 0px 10px",
            margin: mainView != "informes" ? "10px" : "0px",

            padding: "10px",
            borderRadius: "10px 10px 0px 0px",
            background: theme.headerBgHeavy,
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <Tooltip
            title="Invertir resultados de la tabla"
            arrow
            placement="top"
          >
            <div
              onClick={() => handleSort({ id: "id", sortField: "id" })}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                width: "30px",
                height: "32px",
                borderRadius: "10px",
                border: `1px solid ${theme.border}`,
                backgroundColor: isDark ? "#1f2937" : header,
                color: "#FFF",
              }}
            >
              <ChevronUp size={14} strokeWidth={2.5} />
              <ChevronDown size={14} strokeWidth={2.5} />
            </div>
          </Tooltip>

          <span
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: 600,
              letterSpacing: "0.2px",
              color: "#FFF",
            }}
          >
            Mostrando{" "}
            {objPerPage < totalPagesNumber ? objPerPage : totalPagesNumber} de{" "}
            {totalPagesNumber} resultados
          </span>
        </Box>

        {/* TABLE */}
        <Box
          className="tableContainer"
          sx={{
            position: "relative",
            overflowX: "auto",
            ...sxPropContainer,
            boxShadow: isDark
              ? "none"
              : `0 1px 2px rgba(0,0,0,0.05),0 -8px 38px rgba(0,0,0,0.08)`,
            margin: mainView != "informes" ? "10px" : "0px",

            /*  SCROLL PERSONALIZADO */
            "&::-webkit-scrollbar": {
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: isDark
                ? "rgba(255, 255, 255, 0)"
                : "rgba(0, 0, 0, 0)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark
                ? `linear-gradient(90deg, ${header}, rgba(255, 255, 255, 0))`
                : headerHeavy,
              borderRadius: "10px",
              opacity: "0",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: `linear-gradient(90deg, ${header}, ${theme.rowHover})`,
              opacity: "1",
              transition: "all 2s",
            },
          }}
        >
          <table
            {...getTableProps()}
            style={{
              width: tableWidth || "100%",
              minWidth: minTableWidth,
              borderCollapse: "separate",
              borderSpacing: "0 5px",
              borderRadius: "0px 0px 10px 10px",
              fontFamily: "'Roboto', sans-serif",
              padding: "15px",
              margin: minWidth ? "0px auto 10px auto" : "10px",
              backgroundColor: theme.headerBg,
              minHeight: "auto",
            }}
          >
            <thead>
              <tr>
                <th
                  colSpan={visibleColumns.length}
                  style={{ padding: 0, border: "none", position: "sticky" }}
                >
                  <div
                    style={{
                      overflow: "hidden",
                      transition: "all 0.4s ease",
                      maxHeight: selectedFlatRows.length > 0 ? "80px" : "0px",
                      opacity: selectedFlatRows.length > 0 ? 1 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px",
                        transform:
                          selectedFlatRows.length > 0
                            ? "translateY(0px)"
                            : "translateY(-10px)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Tooltip
                          title="Eliminar los registros seleccionados"
                          arrow
                        >
                          <Button
                            startIcon={<DeleteForever />}
                            color="error"
                            size="small"
                            variant="contained"
                            sx={{ boxShadow: "none", height: "33px" }}
                            onClick={() => deleteSelected(selectedFlatRows)}
                          >
                            ELIMINAR SELECCIONADOS
                          </Button>
                        </Tooltip>

                        {selectionActions}
                      </Box>
                    </Box>
                  </div>
                </th>
              </tr>

              {headerGroups.map((hg, hgIndex) => {
                const isColumnHeader = hgIndex === headerGroups.length - 1;
                const { key, ...rowProps } = hg.getHeaderGroupProps();

                return (
                  <tr key={key} {...rowProps}>
                    {hg.headers.map((column) => {
                      const colSpan = column.columns
                        ? column.columns.length
                        : 1;
                      const field = column.sortField || column.id;

                      const isSortedColumn = tableState.sortColumn === field;
                      const direction = tableState.sortDirection;

                      return (
                        <th
                          key={column.id}
                          colSpan={colSpan}
                          onClick={() => {
                            if (
                              isColumnHeader &&
                              !column.disableSortBy &&
                              column.id !== "selection" &&
                              column.render("Header") !== "Acciones"
                            ) {
                              handleSort(column);
                            }
                          }}
                          style={{
                            cursor: field ? "pointer" : "default",
                            textAlign: "center",
                            padding: "10px",
                            fontSize: `${fontSize}px`,
                            borderBottom: `1px solid ${theme.border}`,
                            color: theme.headerText,

                            width: column.width,
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                            }}
                          >
                            {/* TEXTO */}
                            <span>{column.render("Header")}</span>

                            {/* ICONO SORT */}
                            {isColumnHeader &&
                              field &&
                              !column.disableSortBy &&
                              column.id !== "selection" &&
                              column.render("Header") !== "Acciones" && (
                                <div
                                  style={{
                                    position: "absolute",
                                    right: -3,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    lineHeight: 0,
                                  }}
                                >
                                  <ChevronUp
                                    size={14}
                                    strokeWidth={2.5}
                                    style={{
                                      opacity:
                                        isSortedColumn && direction === "ASC"
                                          ? 1
                                          : isSortedColumn
                                            ? 0.3
                                            : 0.4,
                                      transform:
                                        isSortedColumn && direction === "ASC"
                                          ? "scale(1.15)"
                                          : "scale(1)",
                                      transition: "all .15s ease",
                                    }}
                                  />

                                  <ChevronDown
                                    size={14}
                                    strokeWidth={2.5}
                                    style={{
                                      opacity:
                                        isSortedColumn && direction === "DESC"
                                          ? 1
                                          : isSortedColumn
                                            ? 0.3
                                            : 0.4,
                                      transform:
                                        isSortedColumn && direction === "DESC"
                                          ? "scale(1.15)"
                                          : "scale(1)",
                                      transition: "all .15s ease",
                                    }}
                                  />
                                </div>
                              )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>

            <tbody {...getTableBodyProps()}>
              {data.length > 0 &&
                rows.map((row, idx) => {
                  prepareRow(row);

                  return (
                    <tr
                      key={row.id}
                      style={{
                        backgroundColor:
                          idx % 2 === 0 ? theme.rowEven : theme.rowOdd,
                        transition: "all 0.2s",
                        fontSize: `${fontSize}px`,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = theme.rowHover)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor =
                          idx % 2 === 0 ? theme.rowEven : theme.rowOdd)
                      }
                    >
                      {row.cells.map((cell) => (
                        <td
                          key={cell.column.id}
                          style={{
                            textAlign: "center",
                            color: theme.text,
                            fontSize: `${fontSize - 1}px`,

                            filter: blurSensitive ? "blur(6px)" : "none",
                            transition: "filter 0.2s ease",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}

              {/*  FILA DE TOTALES */}
              {tableReports && totalsReport?.length > 0 && data.length > 0 && (
                <>
                  {/* TOTAL PÁGINA (PRIMER ELEMENTO) */}
                  {(() => {
                    const totalPageRow = (
                      <tr
                        style={{
                          background: isDark
                            ? "linear-gradient(90deg, rgba(30,41,59,0.95), rgba(2,6,23,1))"
                            : "#e0f2fe",
                          borderTop: `2px solid ${header}`,
                          borderBottom: `2px solid ${header}`,
                          fontWeight: 600,
                          cursor: totalsReport[0]?.message ? "help" : "default",
                        }}
                      >
                        {visibleColumns.map((column, index) => {
                          let value = totalsReport[0]?.[column.id];

                          if (index === 0) {
                            value =
                              totalsReport[0]?.label ??
                              totalsReport[0]?.[column.id] ??
                              "TOTAL PÁGINA";
                          }

                          return (
                            <td
                              key={column.id}
                              style={{
                                textAlign: "center",
                                color:
                                  index === 0
                                    ? header
                                    : isDark
                                      ? "#e5e7eb"
                                      : "#0f172a",
                                padding: "10px 6px",
                                fontWeight: index === 0 ? 700 : 500,
                                fontSize: `${fontSize}px`,
                              }}
                            >
                              {value ?? ""}
                            </td>
                          );
                        })}
                      </tr>
                    );

                    return totalsReport[0]?.message ? (
                      <Tooltip
                        title={totalsReport[0].message}
                        arrow
                        placement="bottom"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backdropFilter: "blur(8px)",
                              background: isDark
                                ? "rgba(17,24,39,0.85)"
                                : "rgba(255,255,255,0.85)",
                              color: isDark ? "#e5e7eb" : "#1f2937",
                              fontSize: "12px",
                              border: `1px solid ${theme.border}`,
                            },
                          },
                        }}
                      >
                        <tr
                          style={{
                            background: isDark
                              ? "linear-gradient(90deg, rgba(30,41,59,0.95), rgba(2,6,23,1))"
                              : "#e0f2fe",
                            borderTop: `2px solid ${header}`,
                            borderBottom: `2px solid ${header}`,
                            fontWeight: 600,
                            cursor: "help",
                          }}
                        >
                          {visibleColumns.map((column, index) => {
                            let value = totalsReport[0]?.[column.id];

                            if (index === 0) {
                              value =
                                totalsReport[0]?.label ??
                                totalsReport[0]?.[column.id] ??
                                "TOTAL PÁGINA";
                            }

                            return (
                              <td
                                key={column.id}
                                style={{
                                  textAlign: "center",
                                  color:
                                    index === 0
                                      ? header
                                      : isDark
                                        ? "#e5e7eb"
                                        : "#0f172a",
                                  padding: "10px 6px",
                                  fontWeight: index === 0 ? 700 : 500,
                                  fontSize: `${fontSize}px`,
                                }}
                              >
                                {value ?? ""}
                              </td>
                            );
                          })}
                        </tr>
                      </Tooltip>
                    ) : (
                      totalPageRow
                    );
                  })()}

                  {/* DIVIDER */}
                  {totalsReport.length > 1 && (
                    <>
                      <tr>
                        <td colSpan={visibleColumns.length}>
                          <div
                            style={{
                              height: "2px",
                              background: `linear-gradient(90deg, transparent, ${header}, transparent)`,
                              opacity: 0.6,
                            }}
                          />
                        </td>
                      </tr>
                    </>
                  )}

                  {/* HEADER */}
                  <tr>
                    <td
                      colSpan={visibleColumns.length}
                      style={{
                        textAlign: "center",
                        padding: "6px",
                        fontSize: `${fontSize}px`,
                        letterSpacing: "1px",
                        color: isDark ? theme.text : header,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        background: isDark
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(0,0,0,0.04)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                        }}
                      >
                        {/* NO QUITAR: este texto refleja el agregado real de varias fuentes de datos del CRM. */}
                        <span>
                          Totales generales (RECOPILACIÓN DE DATOS DE DISTINTAS
                          FUENTES)
                        </span>

                        {/* BOTÓN INFO */}
                        <Box
                          onClick={handleOpenInfo}
                          sx={{
                            px: 1.5,
                            height: "26px",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            cursor: "pointer",

                            background: isDark
                              ? "rgba(255, 255, 255, 0.23)"
                              : "rgba(0,0,0,0.05)",

                            backdropFilter: "blur(6px)",
                            transition: "all 0.2s ease",

                            "&:hover": {
                              background: isDark
                                ? "rgba(255,255,255,0.12)"
                                : "rgba(0,0,0,0.12)",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <InfoOutlined sx={{ fontSize: 16 }} />
                          AYUDA
                        </Box>
                      </div>
                    </td>
                  </tr>

                  {/* RESTO DE TOTALES */}
                  {totalsReport.slice(1).map((totalRow, rowIndex) => {
                    const accent = header;

                    const glow =
                      viewTitle === "clientes"
                        ? "rgba(167,103,0,0.25)"
                        : viewTitle === "contactos"
                          ? "rgba(13,123,200,0.25)"
                          : viewTitle === "expedientes"
                            ? "rgba(46,125,50,0.25)"
                            : "rgba(100,116,139,0.2)";

                    const rowContent = (
                      <tr
                        style={{
                          background: isDark ? "rgba(2,6,23,0.65)" : "#f8fafc",
                          borderTop: `1px solid ${accent}`,
                          borderBottom: isDark
                            ? "1px solid rgba(255,255,255,0.05)"
                            : "1px solid rgba(0,0,0,0.08)",
                          backdropFilter: isDark ? "blur(6px)" : "none",
                          boxShadow: isDark
                            ? `inset 0 0 10px rgba(255,255,255,0.03), inset 0 0 16px ${glow}`
                            : `inset 0 0 8px rgba(0,0,0,0.04), inset 0 0 14px ${glow}`,
                          fontWeight: 500,
                        }}
                      >
                        {visibleColumns.map((column, index) => {
                          let value = totalRow[column.id];

                          if (index === 0) {
                            value = totalRow.label ?? value;
                          }

                          return (
                            <td
                              key={column.id}
                              style={{
                                cursor: "help",
                                textAlign: "center",
                                color: isDark
                                  ? index === 0
                                    ? accent
                                    : "rgba(229,231,235,0.9)"
                                  : index === 0
                                    ? accent
                                    : "rgba(15,23,42,0.85)",
                                fontSize: `${fontSize}px`,
                                padding: "8px 6px",
                                textShadow:
                                  isDark && index === 0
                                    ? `0 0 8px ${glow}`
                                    : "none",
                                fontWeight: index === 0 ? 600 : 400,
                              }}
                            >
                              {value ?? ""}
                            </td>
                          );
                        })}
                      </tr>
                    );

                    return totalRow.message ? (
                      <Tooltip
                        key={rowIndex}
                        title={totalRow.message}
                        arrow
                        placement="bottom"
                      >
                        {rowContent}
                      </Tooltip>
                    ) : (
                      <React.Fragment key={rowIndex}>
                        {rowContent}
                      </React.Fragment>
                    );
                  })}
                </>
              )}
              {!isLoading && Array.isArray(data) && data.length === 0 && (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    style={{ padding: 0, ...sxDarkColor }}
                  >
                    <Alert
                      severity="info"
                      sx={{
                        // width: "100%",
                        borderRadius: 0,
                        textAlign: "left",
                        fontSize: `${fontSize}px`,
                      }}
                    >
                      No se han encontrado resultados...
                    </Alert>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isDark
                  ? "rgba(2, 6, 23, 0.68)"
                  : "rgba(255, 255, 255, 0.72)",
                backdropFilter: "blur(2px)",
              }}
            >
              <LoadingPage
                isLoading={true}
                heightProp={"220px"}
                widthProp={"100%"}
                colorProp={"#ffffff"}
              />
            </Box>
          )}
        </Box>

        {/* {SubComponentContactsReport && (
          <Box>{SubComponentContactsReport()}</Box>
        )} */}

        {pageCount > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "15px",
              marginBottom: "15px",

              /* Contenedor de la paginación */
              "& .pagination": {
                display: "flex",
                listStyle: "none",
                gap: "6px",
                padding: 0,
                margin: 0,
              },

              /* Botones de página */
              "& .pagination li": {
                cursor: "pointer",
                padding: "0px 8px",
                borderRadius: "8px",
                boxShadow: isDark
                  ? `0 2px 2px rgba(0,0,0,0.8)`
                  : "0 2px 2px rgb(0 0 0 / 58%)",
                backgroundColor: isDark ? theme.rowEven : "#fff",
                color: isDark ? theme.text : header,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "32px",
                height: "32px",
                transition: "all 0.2s ease",
              },

              /* Hacer todo el li clickeable */
              "& .pagination li a, & .pagination li span": {
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                color: "inherit",
              },

              /* Hover de botón */
              "& .pagination li:hover": {
                backgroundColor: isDark ? theme.rowHover : header,
                color: "#fff",
                transform: "translateY(-1px)",
              },

              /* Página activa */
              "& .pagination li.active": {
                backgroundColor: isDark ? theme.rowHover : header,
                color: "#fff",
                borderColor: isDark ? theme.border : header,
                cursor: "default",
                transform: "none",
              },

              /* Deshabilitado (prev/next si los hay) */
              "& .pagination li.disabled": {
                opacity: 0.4,
                pointerEvents: "none",
                cursor: "not-allowed",
              },
            }}
          >
            <ReactPaginate
              nextLabel={<ChevronRight />}
              previousLabel={<ChevronLeft />}
              onPageChange={onPageChange}
              pageCount={pageCount}
              forcePage={forcePage}
              containerClassName="pagination"
              activeClassName="active"
              disabledClassName="disabled"
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              breakLabel="…"
            />
          </Box>
        )}

        <Popover
          disableScrollLock
          open={openInfo}
          anchorEl={infoAnchor}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              handleCloseInfo();
            }
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          slotProps={{
            paper: {
              sx: {
                p: 2,
                maxWidth: "320px",
                borderRadius: "12px",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",

                backgroundColor: isDark
                  ? "rgba(17, 24, 39, 0.6)"
                  : "rgba(255, 255, 255, 0.6)",

                color: isDark ? theme.text : header,
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              },
            },
          }}
        >
          {/* BOTÓN CLOSE */}
          <Box
            onClick={handleCloseInfo}
            sx={{
              ...glassButtonBase,
              ...getGlassButtonTheme(isDark),

              position: "absolute",
              top: 6,
              right: 6,
              color: isDark ? theme.text : header,
            }}
          >
            ✕
          </Box>
          <Typography variant="subtitle2" sx={popoverTitle(isDark)}>
            ¿Qué son estos totales?
          </Typography>
          <Typography variant="body2" sx={popoverText(isDark)}>
            Estos valores representan una agregación de múltiples fuentes de
            datos.
          </Typography>
          <ul style={popoverText(isDark)}>
            <li>Pueden incluir registros históricos y actuales.</li>
            <li>No necesariamente coinciden con los totales de la página.</li>
            <li>Se usan para análisis global consolidado.</li>
          </ul>
          <Typography variant="body2" sx={popoverText(isDark)}>
            Esto sucede debido a que muchos datos no se recopilaron durante
            años, se perdieron y solo los resultados recientes corresponden a
            los estados reales por los que pasa el usuario.
          </Typography>
          <Typography variant="body2" sx={popoverText(isDark)}>
            <Warning color="warning" width={"18px"} /> Interpretar con contexto
            según filtros aplicados.
          </Typography>
        </Popover>
      </div>
    </Fade>
  );
};

export default Table;
